/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { ApolloError, ApolloQueryResult, WatchQueryFetchPolicy } from '@apollo/client';
import { RestLink } from 'apollo-link-rest';
import * as React from 'react';
import { Constants } from '../common/Constants';
import {
    ListApprovalsQuery,
    ListApprovalsQueryVariables,
    useListApprovalsQuery,
} from '../data/rest/queries/ListApprovals.generated';
import {
    ListESignApprovalsQuery,
    ListESignApprovalsQueryVariables,
    useListESignApprovalsQuery,
} from '../data/rest/queries/ListESignApprovals.generated';
import {
    ApprovalCategory,
    ApprovalRequestType,
    ApprovalSortedByKey,
    ApprovalUserRole,
    ListData,
    UserRole,
} from '../data/types.generated';
import { getEsignGatewayProvider } from './EsignUtils';

declare const SUMMARY_URLS: { [approvalType: string]: string };

export function listApprovalsPathBuilder(props: RestLink.PathBuilderProps): string {
    const args = props?.args ?? {};
    const queryParams: string[] = [];

    if (args.approvalType === ApprovalRequestType.Basic) {
        if (args?.userRole) queryParams.push(`userRole=${args.userRole}`);
        if (args?.category !== ApprovalCategory.NotSpecified) queryParams.push(`category=${args.category}`);
        if (args?.isActive) queryParams.push(`isActive=${args.isActive}`);
        if (args?.additionalFilters?.title)
            queryParams.push(`title=${encodeURIComponent(args.additionalFilters.title)}`);
        if (args?.additionalFilters?.details)
            queryParams.push(`details=${encodeURIComponent(args.additionalFilters.details)}`);
        if (args?.additionalFilters?.source)
            queryParams.push(`source=${encodeURIComponent(args.additionalFilters.source)}`);
        if (args?.approvalType) queryParams.push(`approvalType=${args.approvalType}`);
        if (isBasicApproval(args)) queryParams.push(`flowEnvironment=${args.flowEnvironment}`);
        if (args?.excludeThumbnails) queryParams.push(`excludeThumbnails=${args.excludeThumbnails}`);

        // TODO: add search query parameter when it's supported
        // TODO: add sorting query parameter when it's fully supported
        // Use isDescending=true as default for now
        queryParams.push('isDescending=true');

        queryParams.push(`maxNo=${Constants.PAGE_SIZE}`);
    } else {
        const extendedParameters = args.extendedParameters;
        const summaryQueryParams: string[] = [];

        if (extendedParameters) {
            for (const key in extendedParameters) {
                if (Object.prototype.hasOwnProperty.call(extendedParameters, key) && extendedParameters[key]) {
                    summaryQueryParams.push(`${key}=${extendedParameters[key]}`);
                }
            }
        }

        const summaryUrl = SUMMARY_URLS[args.approvalType];
        const fullSummaryUrl =
            summaryQueryParams.length > 0 ? `${summaryUrl}?${summaryQueryParams.join('&')}` : summaryUrl;
        queryParams.push(`summaryUri=${encodeURIComponent(fullSummaryUrl)}`);

        // The extensibility service summary controller defaults to MsApprovals
        // when the Provider query param is omitted. AgentAndAppApprovals and any
        // other non-default provider must send the explicit enum name so the
        // server picks the right Providers:<Name>:{Auth,Headers,Mappings} block.
        if (args.approvalType && args.approvalType !== ApprovalRequestType.MsApprovals) {
            queryParams.push(`Provider=${args.approvalType}`);
        }
    }

    return '?' + queryParams.join('&');
}

export function listESignApprovalsPathBuilder({ args }: RestLink.PathBuilderProps): string {
    if (!args) return '';
    const provider = getEsignGatewayProvider(args.approvalType);
    const endpoint = `/${provider}/signingrequests`;
    const queryParams: string[] = [];

    if (args.search) queryParams.push(`search=${args.search}`);
    if (args.UserRoles) {
        for (const role of args.UserRoles) {
            queryParams.push(`UserRoles=${role}`);
        }
    }
    if (args.orderBy) queryParams.push(`OrderBy=${args.orderBy}`);
    if (args.top) queryParams.push(`Top=${args.top}`);
    if (args.skip) queryParams.push(`Skip=${args.skip}`);
    if (args.nextLink) queryParams.push(`nextLink=${encodeURIComponent(args.nextLink)}`);
    return `${endpoint}?${queryParams.join('&')}`;
}

// Get list data with specified values and use defaults for missing ones
export function getDefaultListData(listData: Partial<ListData>): ListData {
    return {
        userRole: listData?.userRole ?? ApprovalUserRole.Approver,
        category: listData?.category ?? ApprovalCategory.NotSpecified,
        sortedByKey: listData?.sortedByKey ?? ApprovalSortedByKey.NotSpecified,
        approvalType: listData?.approvalType ?? ApprovalRequestType.Basic,
        search: listData?.search ?? null,
        isActive: listData?.isActive ?? null,
        additionalFilters: listData?.additionalFilters ?? null,
        isSortedDescending: listData?.isSortedDescending ?? null,
        extendedParameters: listData?.extendedParameters ?? null,
        excludeThumbnails: listData?.excludeThumbnails ?? null,
    };
}

export interface UseApprovalsQueryResult {
    data: ListApprovalsQuery;
    loading: boolean;
    error: ApolloError;
    hasMoreItems: boolean;
    fetchMoreItems: () => void;
    refetch: () => Promise<ApolloQueryResult<any>>;
}

/**
 * Call this in React component to fetch approvals data, it combines active approvals data and completed approvals data.
 * Currently, when query variables change, cached page data will be used to display the list first, then be overwritten
 * by the new data from service. Following task is created for looking into how to better load/update cached page data
 * to create a smooth experience with less UX flicker.
 * TODO: https://domoreexp.visualstudio.com/MSTeams/_workitems/edit/903711/
 *
 * @param listData query parameters to get the approval list
 * @param fetchPolicy Apollo Client fetch policy to get the approval list
 * @param skip if the data query should be skipped
 */
export function useApprovalsQuery(
    listData: Partial<ListData>,
    fetchPolicy: WatchQueryFetchPolicy,
    skip?: boolean
): UseApprovalsQueryResult {
    // Use this to flag ongoing fetchMore requests to prevent duplicated data from merging in.
    const fetching = React.useRef<boolean>(false);

    // Use this to track if fetchmore is ready, i.e. new query with different variables has been received,
    // and useQuery is not returning cached data anymore.
    const fetchMoreReady = React.useRef<boolean>(false);

    // The statusCounter will keep track the version of current set of variables.
    // This can be used to invalidate fetchmore pagination data, as Apollo
    // will try to update the cache when pagination data is returned
    // from service, even after the query variables changes.
    const statusCounter = React.useRef(0);

    const variables = React.useMemo((): ListApprovalsQueryVariables => {
        statusCounter.current += 1;
        fetching.current = false;
        fetchMoreReady.current = false;

        return {
            userRole: listData?.userRole ?? ApprovalUserRole.Approver,
            approvalType: listData?.approvalType ?? ApprovalRequestType.Basic,
            category: listData?.category ?? ApprovalCategory.NotSpecified,
            isActive: listData?.isActive ?? null,
            additionalFilters: listData?.additionalFilters ?? null,
            flowEnvironment: listData?.flowEnvironment ?? null,
            extendedParameters: listData?.extendedParameters ?? {},
            excludeThumbnails: listData?.excludeThumbnails ?? null,
            pathBuilder: listApprovalsPathBuilder,
        };
    }, [
        listData?.category,
        listData?.isActive,
        listData?.additionalFilters,
        listData?.userRole,
        listData?.flowEnvironment,
        listData?.approvalType,
        listData?.extendedParameters,
        listData?.excludeThumbnails,
    ]);

    const {
        data,
        loading: dataLoading,
        error,
        fetchMore,
        refetch,
    } = useListApprovalsQuery({
        variables,
        fetchPolicy,
        nextFetchPolicy: 'cache-first',
        skip,
        notifyOnNetworkStatusChange: false,
    });

    // If data.ListApprovals is returned as null, it means the data is not available yet.
    // Set loading as true in this case.
    // The value of error will stay the same.
    const loading = dataLoading || data?.ListApprovals === null;

    // Set fetchMoreReady if first page of data has been received
    const value = data?.ListApprovals?.value;
    if (!loading && !fetchMoreReady.current && value && value.length <= Constants.PAGE_SIZE) {
        fetchMoreReady.current = true;
    }

    // Has more items to load if there's pagination cursor and fetchmore is ready
    const hasMoreItems = !!data?.ListApprovals?.nextLink && fetchMoreReady.current;

    const fetchMoreItems = React.useCallback((): void => {
        const cursor = data?.ListApprovals?.nextLink;

        const updateQuery = (
            previousResult: ListApprovalsQuery,
            {
                fetchMoreResult,
                variables,
            }: { fetchMoreResult: ListApprovalsQuery; variables: ListApprovalsQueryVariables }
        ): ListApprovalsQuery => {
            fetching.current = false;

            // Apollo will try to update cache from current query with fetchmore data that
            // belongs to a previous query with different variables.
            // https://github.com/apollographql/apollo-client/issues/2813
            // If the variables used for this pagination call belong to
            // a previous version of variables, drop the data and don't use it.
            if (variables.status !== statusCounter.current) {
                return previousResult;
            }

            return {
                ListApprovals: {
                    __typename: fetchMoreResult?.ListApprovals?.__typename,
                    nextLink: fetchMoreResult?.ListApprovals?.nextLink,
                    value: [
                        ...(previousResult?.ListApprovals?.value || []),
                        ...(fetchMoreResult?.ListApprovals?.value || []),
                    ],
                    config: previousResult?.ListApprovals?.config,
                },
            };
        };

        if (!fetching.current && cursor) {
            fetching.current = true;
            fetchMore({
                context: {
                    [Constants.PAGE_LINK_HEADER]: cursor,
                },
                variables: {
                    status: statusCounter.current,
                },
                updateQuery,
            });
        }
    }, [data, fetchMore]);

    if (skip) {
        return {
            data,
            loading: dataLoading,
            error,
            hasMoreItems: false,
            fetchMoreItems: (): void => null,
            refetch,
        };
    }

    return {
        data,
        loading,
        error,
        hasMoreItems,
        fetchMoreItems,
        refetch,
    };
}

export interface UseESignApprovalsQueryResult {
    data: ListESignApprovalsQuery;
    loading: boolean;
    error: ApolloError;
    hasMoreItems: boolean;
    fetchMoreItems: () => void;
    refetch: () => Promise<ApolloQueryResult<any>>;
}

/**
 * Call this in React component to fetch eSign approvals data, it combines active approvals data and completed approvals data.
 *
 * @param listData query parameters to get the approval list
 * @param fetchPolicy Apollo Client fetch policy to get the approval list
 * @param skip if the data query should be skipped
 */
export function useESignApprovalsQuery(
    listData: Partial<ListData>,
    fetchPolicy: WatchQueryFetchPolicy,
    skip?: boolean
): UseESignApprovalsQueryResult {
    // Use this to flag ongoing fetchMore requests to prevent duplicated data from merging in.
    const fetching = React.useRef<boolean>(false);

    // Use this to track if fetchmore is ready, i.e. new query with different variables has been received,
    // and useQuery is not returning cached data anymore.
    const fetchMoreReady = React.useRef<boolean>(false);

    // The statusCounter will keep track the version of current set of variables.
    // This can be used to invalidate fetchmore pagination data, as Apollo
    // will try to update the cache when pagination data is returned
    // from service, even after the query variables changes.
    const statusCounter = React.useRef(0);

    const variables = React.useMemo((): ListESignApprovalsQueryVariables => {
        statusCounter.current += 1;
        fetching.current = false;
        fetchMoreReady.current = false;

        return {
            search: `${(listData?.additionalFilters?.title && `${listData.additionalFilters?.title}`) || ''}`,
            UserRoles: getESignUserRoles(listData?.userRole, listData?.approvalType),
            orderBy: 'date desc',
            skip: 0,
            top: Constants.PAGE_SIZE,
            approvalType: listData?.approvalType,
            pathBuilder: listESignApprovalsPathBuilder,
        };
    }, [listData?.approvalType, listData.additionalFilters?.title, listData?.userRole]);

    const {
        data,
        loading: dataLoading,
        error,
        fetchMore,
        refetch,
    } = useListESignApprovalsQuery({
        variables,
        fetchPolicy,
        nextFetchPolicy: 'cache-first',
        skip,
        notifyOnNetworkStatusChange: false,
    });

    // If data.ListESignApprovals is returned as null, it means the data is not available yet.
    // Set loading as true in this case.
    // The value of error will stay the same.
    const loading = dataLoading || data?.ListESignApprovals === null;

    // Set fetchMoreReady if first page of data has been received
    const signingRequests = data?.ListESignApprovals?.signingRequests;
    if (!loading && !fetchMoreReady.current && signingRequests?.length <= Constants.PAGE_SIZE) {
        fetchMoreReady.current = true;
    }

    // Has more items to load if there's pagination cursor and fetchmore is ready
    const hasMoreItems = !!data?.ListESignApprovals?.nextLink && fetchMoreReady.current;

    const fetchMoreItems = React.useCallback((): void => {
        const cursor = data?.ListESignApprovals?.nextLink;

        const updateQuery = (
            previousResult: ListESignApprovalsQuery,
            {
                fetchMoreResult,
                variables,
            }: { fetchMoreResult: ListESignApprovalsQuery; variables: ListESignApprovalsQueryVariables }
        ): ListESignApprovalsQuery => {
            fetching.current = false;

            // Apollo will try to update cache from current query with fetchmore data that
            // belongs to a previous query with different variables.
            // https://github.com/apollographql/apollo-client/issues/2813
            // If the variables used for this pagination call belong to
            // a previous version of variables, drop the data and don't use it.
            if (variables.status !== statusCounter.current) {
                return previousResult;
            }

            return {
                ListESignApprovals: {
                    __typename: fetchMoreResult?.ListESignApprovals?.__typename,
                    nextLink: fetchMoreResult?.ListESignApprovals?.nextLink,
                    signingRequests: [
                        ...(previousResult?.ListESignApprovals?.signingRequests || []),
                        ...(fetchMoreResult?.ListESignApprovals?.signingRequests || []),
                    ],
                },
            };
        };

        if (!fetching.current && cursor) {
            fetching.current = true;
            fetchMore({
                context: {
                    [Constants.PAGE_LINK_HEADER]: cursor,
                },
                variables: {
                    status: statusCounter.current,
                    skip: data.ListESignApprovals?.signingRequests?.length,
                    nextLink: data.ListESignApprovals?.nextLink,
                },
                updateQuery,
            });
        }
    }, [data, fetchMore]);

    if (skip) {
        return {
            data,
            loading: dataLoading,
            error,
            hasMoreItems: false,
            fetchMoreItems: (): void => null,
            refetch,
        };
    }

    return {
        data,
        loading,
        error,
        hasMoreItems,
        fetchMoreItems,
        refetch,
    };
}

export function getESignUserRoles(
    approvalUserRole: ApprovalUserRole,
    approvalRequestType: ApprovalRequestType
): UserRole[] {
    if (!approvalUserRole || !approvalRequestType) {
        return [];
    }
    return approvalUserRole === ApprovalUserRole.Owner
        ? [UserRole.Sender]
        : approvalRequestType === ApprovalRequestType.Syntex
        ? [UserRole.Signer]
        : [UserRole.Approver, UserRole.Signer, UserRole.CarbonCopy];
}

function isBasicApproval(args: { [key: string]: any }): boolean {
    return args?.approvalType === ApprovalRequestType.Basic && args?.flowEnvironment;
}
